// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { cashFlowService, recurringTransactionService } from '../../services/financeService';
import { CashFlowProjection, RecurringTransaction, CashFlowProjectionItem } from '../../types/finance';
import { toast } from 'react-hot-toast';
import { FaPlus, FaTrash, FaSave, FaChartLine } from 'react-icons/fa';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const CashFlowForecaster: React.FC = () => {
  const { user } = useAuth();
  const [projections, setProjections] = useState<CashFlowProjection[]>([]);
  const [recurringTransactions, setRecurringTransactions] = useState<RecurringTransaction[]>([]);
  const [selectedProjection, setSelectedProjection] = useState<CashFlowProjection | null>(null);
  const [projectionItems, setProjectionItems] = useState<CashFlowProjectionItem[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Form states
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [initialBalance, setInitialBalance] = useState(0);
  const [newItem, setNewItem] = useState<Partial<CashFlowProjectionItem>>({
    date: '',
    description: '',
    amount: 0,
    type: 'income',
    is_recurring: false
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [projectionsData, transactionsData] = await Promise.all([
          cashFlowService.getAll(),
          recurringTransactionService.getAll()
        ]);
        setProjections(projectionsData);
        setRecurringTransactions(transactionsData);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load cash flow data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSelectProjection = async (id: string) => {
    try {
      const projection = await cashFlowService.getById(id);
      setSelectedProjection(projection);
      setProjectionItems(projection.items || []);
    } catch (error) {
      console.error('Error fetching projection:', error);
      toast.error('Failed to load projection details');
    }
  };

  const handleCreateProjection = async () => {
    if (!user) return;
    
    try {
      // Calculate final balance based on initial balance and all items
      const totalIncome = projectionItems
        .filter(item => item.type === 'income')
        .reduce((sum, item) => sum + Number(item.amount), 0);
      
      const totalExpenses = projectionItems
        .filter(item => item.type === 'expense')
        .reduce((sum, item) => sum + Number(item.amount), 0);
      
      const finalBalance = initialBalance + totalIncome - totalExpenses;
      
      const newProjection: CashFlowProjection = {
        user_id: user.id,
        name,
        start_date: startDate,
        end_date: endDate,
        initial_balance: initialBalance,
        final_balance: finalBalance
      };
      
      const savedProjection = await cashFlowService.create(newProjection, projectionItems);
      setProjections([savedProjection, ...projections]);
      setSelectedProjection(savedProjection);
      setShowForm(false);
      resetForm();
      toast.success('Cash flow projection created successfully');
    } catch (error) {
      console.error('Error creating projection:', error);
      toast.error('Failed to create cash flow projection');
    }
  };

  const handleUpdateProjection = async () => {
    if (!selectedProjection || !user) return;
    
    try {
      // Calculate final balance based on initial balance and all items
      const totalIncome = projectionItems
        .filter(item => item.type === 'income')
        .reduce((sum, item) => sum + Number(item.amount), 0);
      
      const totalExpenses = projectionItems
        .filter(item => item.type === 'expense')
        .reduce((sum, item) => sum + Number(item.amount), 0);
      
      const finalBalance = initialBalance + totalIncome - totalExpenses;
      
      const updatedProjection: Partial<CashFlowProjection> = {
        name,
        start_date: startDate,
        end_date: endDate,
        initial_balance: initialBalance,
        final_balance: finalBalance
      };
      
      const savedProjection = await cashFlowService.update(
        selectedProjection.id!,
        updatedProjection,
        projectionItems
      );
      
      setProjections(projections.map(p => 
        p.id === savedProjection.id ? savedProjection : p
      ));
      setSelectedProjection(savedProjection);
      toast.success('Cash flow projection updated successfully');
    } catch (error) {
      console.error('Error updating projection:', error);
      toast.error('Failed to update cash flow projection');
    }
  };

  const handleDeleteProjection = async () => {
    if (!selectedProjection) return;
    
    try {
      await cashFlowService.delete(selectedProjection.id!);
      setProjections(projections.filter(p => p.id !== selectedProjection.id));
      setSelectedProjection(null);
      setProjectionItems([]);
      resetForm();
      toast.success('Cash flow projection deleted successfully');
    } catch (error) {
      console.error('Error deleting projection:', error);
      toast.error('Failed to delete cash flow projection');
    }
  };

  const handleAddItem = () => {
    if (!newItem.date || !newItem.description || !newItem.amount) {
      toast.error('Please fill in all item fields');
      return;
    }
    
    const item: CashFlowProjectionItem = {
      projection_id: selectedProjection?.id || '',
      date: newItem.date!,
      description: newItem.description!,
      amount: Number(newItem.amount),
      type: newItem.type as 'income' | 'expense',
      is_recurring: Boolean(newItem.is_recurring),
      recurring_transaction_id: newItem.recurring_transaction_id
    };
    
    setProjectionItems([...projectionItems, item]);
    setNewItem({
      date: '',
      description: '',
      amount: 0,
      type: 'income',
      is_recurring: false
    });
  };

  const handleRemoveItem = (index: number) => {
    const updatedItems = [...projectionItems];
    updatedItems.splice(index, 1);
    setProjectionItems(updatedItems);
  };

  const handleNewProjection = () => {
    setSelectedProjection(null);
    setProjectionItems([]);
    resetForm();
    setShowForm(true);
  };

  const resetForm = () => {
    setName('');
    setStartDate('');
    setEndDate('');
    setInitialBalance(0);
    setNewItem({
      date: '',
      description: '',
      amount: 0,
      type: 'income',
      is_recurring: false
    });
  };

  const generateChartData = () => {
    if (!selectedProjection) return null;
    
    // Sort items by date
    const sortedItems = [...projectionItems].sort((a, b) => 
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    
    // Generate dates between start and end date
    const start = new Date(selectedProjection.start_date);
    const end = new Date(selectedProjection.end_date);
    const dateLabels: string[] = [];
    const balances: number[] = [];
    
    let currentBalance = selectedProjection.initial_balance;
    let currentDate = new Date(start);
    
    while (currentDate <= end) {
      const dateString = currentDate.toISOString().split('T')[0];
      dateLabels.push(dateString);
      
      // Find items for this date
      const dayItems = sortedItems.filter(item => item.date === dateString);
      
      // Update balance based on items
      dayItems.forEach(item => {
        if (item.type === 'income') {
          currentBalance += Number(item.amount);
        } else {
          currentBalance -= Number(item.amount);
        }
      });
      
      balances.push(currentBalance);
      
      // Move to next day
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return {
      labels: dateLabels,
      datasets: [
        {
          label: 'Cash Balance',
          data: balances,
          borderColor: 'rgb(75, 192, 192)',
          tension: 0.1,
          fill: false
        }
      ]
    };
  };

  const chartData = generateChartData();

  return (
    <div className="relative container mx-auto p-6 overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-300 to-indigo-400 rounded-full filter blur-3xl opacity-20 -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tr from-indigo-300 to-purple-300 rounded-full filter blur-3xl opacity-20 translate-x-1/3 translate-y-1/3"></div>
      
      <div className="relative z-10 flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Cash Flow Forecaster</h1>
        <button
          onClick={handleNewProjection}
          className="relative overflow-hidden bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-5 py-2.5 rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] flex items-center transition-all duration-200 group"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
          <span className="relative z-10 flex items-center">
            <FaPlus className="mr-2 group-hover:rotate-90 transition-transform duration-200" /> 
            <span>New Projection</span>
          </span>
        </button>
      </div>

      {loading ? (
        <div className="relative backdrop-blur-sm bg-white/20 border border-white/30 rounded-xl shadow-lg p-8 text-center">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-indigo-50/30 opacity-80"></div>
          <div className="relative z-10 flex flex-col items-center">
            <div className="w-10 h-10 border-4 border-t-blue-500 border-blue-200 rounded-full animate-spin mb-4"></div>
            <p className="text-lg font-medium text-blue-700">Loading your projections...</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left sidebar - Projections list */}
          <div className="relative overflow-hidden backdrop-blur-sm bg-white/20 border border-white/30 rounded-xl shadow-lg p-6">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-indigo-50/30 opacity-80"></div>
            
            <div className="relative z-10">
              <h2 className="text-xl font-bold mb-5 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Your Projections</h2>
              {projections.length === 0 ? (
                <div className="text-center py-4 bg-white/30 backdrop-blur-sm rounded-xl border border-white/40">
                  <p className="text-gray-700">No projections yet. Create your first one!</p>
                </div>
              ) : (
                <ul className="space-y-3">
                  {projections.map(projection => (
                    <li 
                      key={projection.id} 
                      className={`relative overflow-hidden backdrop-blur-sm rounded-xl border cursor-pointer transition-all duration-200 transform hover:scale-[1.02] ${
                        selectedProjection?.id === projection.id 
                          ? 'bg-blue-500/10 border-blue-200/50 shadow-md' 
                          : 'bg-white/30 border-white/40 hover:bg-blue-50/30 hover:border-blue-100/40'
                      }`}
                      onClick={() => handleSelectProjection(projection.id!)}
                    >
                      <div className="p-4">
                        <div className="font-medium text-lg text-gray-800">{projection.name}</div>
                        <div className="text-sm text-indigo-600 mt-1">
                          {new Date(projection.start_date).toLocaleDateString()} to {new Date(projection.end_date).toLocaleDateString()}
                        </div>
                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-blue-100/30">
                          <span className="text-sm font-medium text-gray-700">Final Balance:</span>
                          <span className={`text-sm font-bold px-2 py-1 rounded-lg ${
                            projection.final_balance >= 0 
                              ? 'bg-green-100/70 text-green-700' 
                              : 'bg-red-100/70 text-red-700'
                          }`}>
                            ${projection.final_balance.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Main content */}
          <div className="lg:col-span-2">
            {showForm || selectedProjection ? (
              <div className="relative overflow-hidden backdrop-blur-sm bg-white/20 border border-white/30 rounded-xl shadow-lg p-8">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-indigo-50/30 opacity-80"></div>
                <div className="absolute top-0 right-0 w-40 h-40 bg-blue-400/10 rounded-full filter blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-40 h-40 bg-indigo-400/10 rounded-full filter blur-3xl"></div>
                
                <div className="relative z-10">
                  <h2 className="text-2xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                    {selectedProjection ? 'Edit Projection' : 'New Projection'}
                  </h2>
                
                {/* Projection form */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
                  <div>
                    <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 mb-2">Projection Name</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full px-4 py-2.5 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-gray-800"
                      placeholder="Projection name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 mb-2">Initial Balance ($)</label>
                    <input
                      type="number"
                      value={initialBalance}
                      onChange={(e) => setInitialBalance(Number(e.target.value))}
                      className="w-full px-4 py-2.5 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-gray-800"
                      placeholder="0.00"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 mb-2">Start Date</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full px-4 py-2.5 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-gray-800"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 mb-2">End Date</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full px-4 py-2.5 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-gray-800"
                    />
                  </div>
                </div>

                {/* Cash flow items */}
                <div className="mb-8">
                  <h3 className="text-xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Cash Flow Items</h3>
                  
                  {/* Add new item form */}
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-5 relative overflow-hidden backdrop-blur-sm bg-white/30 border border-white/40 p-5 rounded-xl shadow-sm">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-50/10 to-indigo-50/10 opacity-70"></div>
                    
                    <div className="relative z-10">
                      <label className="block text-xs font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 mb-2">Date</label>
                      <input
                        type="date"
                        value={newItem.date}
                        onChange={(e) => setNewItem({...newItem, date: e.target.value})}
                        className="w-full px-3 py-2 bg-white/50 backdrop-blur-sm border border-white/40 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-gray-800 text-sm"
                      />
                    </div>
                    <div className="md:col-span-2 relative z-10">
                      <label className="block text-xs font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 mb-2">Description</label>
                      <input
                        type="text"
                        value={newItem.description}
                        onChange={(e) => setNewItem({...newItem, description: e.target.value})}
                        className="w-full px-3 py-2 bg-white/50 backdrop-blur-sm border border-white/40 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-gray-800 text-sm"
                        placeholder="Item description"
                      />
                    </div>
                    <div className="relative z-10">
                      <label className="block text-xs font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 mb-2">Amount ($)</label>
                      <input
                        type="number"
                        value={newItem.amount}
                        onChange={(e) => setNewItem({...newItem, amount: Number(e.target.value)})}
                        className="w-full px-3 py-2 bg-white/50 backdrop-blur-sm border border-white/40 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-gray-800 text-sm"
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="relative z-10">
                      <label className="block text-xs font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 mb-2">Type</label>
                      <select
                        value={newItem.type}
                        onChange={(e) => setNewItem({...newItem, type: e.target.value as 'income' | 'expense'})}
                        className="w-full px-3 py-2 bg-white/50 backdrop-blur-sm border border-white/40 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-gray-800 text-sm"
                      >
                        <option value="income">Income</option>
                        <option value="expense">Expense</option>
                      </select>
                    </div>
                    <div className="md:col-span-4 flex items-center mt-3 relative z-10">
                      <label className="inline-flex items-center p-2 bg-white/40 backdrop-blur-sm border border-white/40 rounded-lg hover:bg-white/50 transition-colors cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newItem.is_recurring}
                          onChange={(e) => setNewItem({...newItem, is_recurring: e.target.checked})}
                          className="form-checkbox h-4 w-4 text-blue-600 rounded"
                        />
                        <span className="ml-2 text-sm font-medium text-indigo-700">Recurring Transaction</span>
                      </label>
                      
                      {newItem.is_recurring && (
                        <select
                          value={newItem.recurring_transaction_id || ''}
                          onChange={(e) => setNewItem({...newItem, recurring_transaction_id: e.target.value})}
                          className="ml-4 px-3 py-1.5 bg-white/50 backdrop-blur-sm border border-white/40 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors text-gray-800 text-sm"
                        >
                          <option value="">Select a recurring transaction</option>
                          {recurringTransactions.map(tx => (
                            <option key={tx.id} value={tx.id}>
                              {tx.name} (${tx.amount} - {tx.frequency})
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    <div className="md:col-span-1 flex items-center justify-end mt-3 relative z-10">
                      <button
                        onClick={handleAddItem}
                        className="relative overflow-hidden bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-lg shadow-sm hover:shadow-md hover:scale-105 transition-all duration-200 text-sm flex items-center"
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-500 opacity-0 hover:opacity-100 transition-opacity duration-200"></div>
                        <span className="relative z-10">Add Item</span>
                      </button>
                    </div>
                  </div>
                  
                  {/* Items list */}
                  {projectionItems.length > 0 ? (
                    <div className="relative overflow-hidden backdrop-blur-sm bg-white/30 border border-white/40 rounded-xl shadow-sm">
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/10 to-indigo-50/10 opacity-70"></div>
                      
                      <div className="relative z-10 overflow-x-auto">
                        <table className="min-w-full divide-y divide-blue-100/30">
                          <thead className="bg-gradient-to-r from-blue-50/70 to-indigo-50/70">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-blue-700 uppercase tracking-wider">Date</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-blue-700 uppercase tracking-wider">Description</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-blue-700 uppercase tracking-wider">Amount</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-blue-700 uppercase tracking-wider">Type</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-blue-700 uppercase tracking-wider">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-blue-100/20">
                            {projectionItems.map((item, index) => (
                              <tr key={index} className="hover:bg-blue-50/30 transition-colors duration-150">
                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-700">{item.date}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{item.description}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                                  <span className={`px-3 py-1 rounded-lg ${item.type === 'income' ? 'bg-green-50/70 text-green-700' : 'bg-red-50/70 text-red-700'}`}>
                                    ${Number(item.amount).toFixed(2)}
                                  </span>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm">
                                  <span className={`px-3 py-1 rounded-lg text-xs font-medium ${item.type === 'income' ? 'bg-green-100/70 text-green-800' : 'bg-red-100/70 text-red-800'}`}>
                                    {item.type}
                                  </span>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm">
                                  <button
                                    onClick={() => handleRemoveItem(index)}
                                    className="bg-red-50/50 text-red-600 hover:text-red-800 p-1.5 rounded-lg transition-colors duration-200 hover:bg-red-100/70"
                                  >
                                    <FaTrash size={14} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="relative overflow-hidden backdrop-blur-sm bg-white/20 border border-white/30 rounded-xl shadow-sm p-6 text-center">
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/20 to-indigo-50/20 opacity-70"></div>
                      <p className="text-gray-700 relative z-10">No items added yet. Add your first income or expense item above.</p>
                    </div>
                  )}
                </div>

                {/* Action buttons */}
                <div className="flex flex-wrap justify-end space-x-3 mt-8">
                  {selectedProjection && (
                    <button
                      onClick={handleDeleteProjection}
                      className="relative overflow-hidden bg-gradient-to-r from-red-500 to-pink-600 text-white px-5 py-2.5 rounded-xl shadow-lg hover:shadow-xl flex items-center transition-all duration-200 group"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-red-400 to-pink-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                      <span className="relative z-10 flex items-center">
                        <FaTrash className="mr-2 group-hover:scale-110 transition-transform duration-200" /> Delete
                      </span>
                    </button>
                  )}
                  <button
                    onClick={selectedProjection ? handleUpdateProjection : handleCreateProjection}
                    className="relative overflow-hidden bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-5 py-2.5 rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] flex items-center transition-all duration-200 group"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                    <span className="relative z-10 flex items-center">
                      <FaSave className="mr-2 group-hover:scale-110 transition-transform duration-200" /> {selectedProjection ? 'Update' : 'Save'}
                    </span>
                  </button>
                </div>
                </div>
              </div>
            ) : (
              <div className="relative overflow-hidden backdrop-blur-sm bg-white/20 border border-white/30 rounded-xl shadow-lg p-10 text-center">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-indigo-50/30 opacity-70"></div>
                <div className="absolute -top-20 left-1/2 transform -translate-x-1/2 w-40 h-40 bg-indigo-300/20 rounded-full filter blur-3xl"></div>
                <div className="absolute -bottom-20 left-1/2 transform -translate-x-1/2 w-40 h-40 bg-blue-300/20 rounded-full filter blur-3xl"></div>
                
                <div className="relative z-10">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 border border-white/40 flex items-center justify-center shadow-md">
                    <FaChartLine className="text-blue-600 text-4xl" />
                  </div>
                  <h2 className="text-2xl font-bold mb-3 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Cash Flow Forecaster</h2>
                  <p className="text-gray-700 mb-8 max-w-lg mx-auto">
                    Create cash flow projections to forecast your business finances over time.
                    Track income, expenses, and see your projected cash balance.
                  </p>
                  <button
                    onClick={handleNewProjection}
                    className="relative overflow-hidden bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3 rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] inline-flex items-center transition-all duration-200 group"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                    <span className="relative z-10 flex items-center">
                      <FaPlus className="mr-2 group-hover:rotate-90 transition-transform duration-200" /> Create Your First Projection
                    </span>
                  </button>
                </div>
              </div>
            )}

            {/* Chart */}
            {selectedProjection && chartData && (
              <div className="relative overflow-hidden backdrop-blur-sm bg-white/20 border border-white/30 rounded-xl shadow-lg p-6 mt-8">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-indigo-50/30 opacity-80"></div>
                <div className="absolute top-0 right-0 w-40 h-40 bg-blue-300/10 rounded-full filter blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-40 h-40 bg-indigo-300/10 rounded-full filter blur-3xl"></div>
                
                <div className="relative z-10">
                  <h3 className="text-xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Cash Flow Projection Chart</h3>
                  
                  <div className="bg-white/40 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm p-4 h-64">
                    <Line 
                      data={chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: false,
                            grid: {
                              color: 'rgba(100, 116, 139, 0.1)',
                            },
                            title: {
                              display: true,
                              text: 'Cash Balance ($)',
                              color: '#4338ca'
                            },
                            ticks: {
                              color: '#334155'
                            }
                          },
                          x: {
                            grid: {
                              color: 'rgba(100, 116, 139, 0.1)',
                            },
                            title: {
                              display: true,
                              text: 'Date',
                              color: '#4338ca'
                            },
                            ticks: {
                              color: '#334155',
                              maxRotation: 45,
                              minRotation: 45
                            }
                          }
                        },
                        plugins: {
                          legend: {
                            labels: {
                              color: '#334155',
                              font: {
                                weight: 'bold'
                              }
                            }
                          },
                          tooltip: {
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            titleColor: '#1e40af',
                            bodyColor: '#334155',
                            borderColor: '#93c5fd',
                            borderWidth: 1,
                            padding: 10,
                            cornerRadius: 8,
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CashFlowForecaster;
